// Geophysics module (WASM-powered)
import init, {
  get_geo_defaults, run_seismic_scan, run_stress_scan,
  run_heatflow_scan, run_gravity_scan, run_earthquake_scan
} from './engines.js';
import { getLicense, hasFullAccess } from './auth.js';
import { GeoNum, geo } from './geo-num.js';
import { ZONES_GEOPHYSICS } from './geo-num-zones.js';
import { registerReport } from './report-gen.js';

const _MODULE = 'Geophysics';
const charts = {};
let ready = false, defaults = null;

// Animation state
const animData = {};

export async function initWasm() {
  try {
    await init();
    ready = true;
    defaults = JSON.parse(get_geo_defaults());
    populateDefaults();
    // Configure GeoNum for geophysics domain (km to Earth scale)
    GeoNum.setZoneConfig(ZONES_GEOPHYSICS.boundaries, ZONES_GEOPHYSICS.minZone);
  }
  catch (err) { console.error('WASM init (geo):', err); }
}

// Auto-run the matching scan the first time a basic geo tab is opened while its
// table is still empty, so the panel shows results instead of an empty table
// (mirrors the research tabs, which already auto-run on open).
const _autoScan = { seismic: seismicScan, stress: stressScan, heatflow: heatflowScan, gravity: gravityScan, earthquake: earthquakeScan };

export function init_ui() {
  document.querySelectorAll('.geotab').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.geotab').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('.geo-panel').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      document.getElementById('geop-' + btn.dataset.geo).classList.add('active');
      const fn = _autoScan[btn.dataset.geo];
      const tbl = document.getElementById('t-' + btn.dataset.geo);
      if (ready && fn && tbl && !tbl.querySelector('tbody tr')) { try { fn(); } catch (e) { console.error('geo auto-scan:', e); } }
    });
  });
  bind('btn-seismic', seismicScan);
  bind('btn-stress', stressScan);
  bind('btn-heatflow', heatflowScan);
  bind('btn-gravity', gravityScan);
  bind('btn-earthquake', earthquakeScan);
}

export function applyGeoGating() {
  const license = getLicense();
  const tier = license?.tier || 'trial';
  const has = hasFullAccess(tier) || license?.modules?.includes('geophysics') || (tier === 'trial' && license?.isActive);
  const g = document.getElementById('geo-gate');
  const c = document.getElementById('geo-content');
  if (g && c) {
    if (has) { g.classList.add('hidden'); c.classList.remove('hidden'); }
    else { g.classList.remove('hidden'); c.classList.add('hidden'); }
  }
}

function populateDefaults() {
  if (!defaults) return;
  setTA('in-seismic', defaults.seismic?.layers);
  setTA('in-stress', defaults.stress?.plates);
  setTA('in-heatflow', defaults.heatflow?.regions);
  setTA('in-gravity', defaults.gravity?.surveys);
  setTA('in-earthquake', defaults.earthquake?.zones);
}

function setTA(id, v) { const el = document.getElementById(id); if (el && v) el.value = JSON.stringify(v, null, 2); }
function bind(id, fn) { const el = document.getElementById(id); if (el) el.addEventListener('click', async () => { if (!ready) return; el.disabled = true; window.gdbsShowLoading && window.gdbsShowLoading('Computing...', (el.textContent || '').trim()); await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r))); try { await fn(); } finally { window.gdbsHideLoading && window.gdbsHideLoading(); el.disabled = false; } }); }
function status(id, m) { const el = document.getElementById('s-' + id); if (el) el.textContent = m; }
function fmtSci(v) { return typeof v === 'number' ? (Math.abs(v) > 1e6 || (Math.abs(v) < 0.001 && v !== 0) ? v.toExponential(3) : v.toFixed(4)) : v; }
function tierColor(val) {
  const v = parseFloat(val); let r, g, b;
  if (v < 0.5) { const t = v / 0.5; r = 220; g = Math.round(50 + 170 * t); b = 50; }
  else { const t = (v - 0.5) / 0.5; r = Math.round(220 - 180 * t); g = 200; b = Math.round(50 + 30 * t); }
  return `rgb(${r},${g},${b})`;
}
function renderGradientBar(tc) {
  if (!tc) return '';
  const base = [{ val: tc.core, name: 'Core' }, { val: tc.magnitude, name: 'Mag' }, { val: tc.phase, name: 'Phase' }, { val: tc.proportion, name: 'Prop' }];
  const rel = [];
  if (tc.relational) { for (let i = 0; i < tc.relational.length; i++) { const r = tc.relational[i], l = i + 2; rel.push({ val: r[0], name: `Mag\u00D7${l}` }, { val: r[1], name: `Phase\u00D7${l}` }, { val: r[2], name: `Prop\u00D7${l}` }); } }
  const all = [...base, ...rel], pct = (100 / all.length).toFixed(2);
  let h = '<div class="tc-wrap"><div class="tc-bar">';
  for (const t of all) h += `<div class="tc-seg" style="width:${pct}%;background:${tierColor(Math.max(0.08, t.val))}"><span class="tc-tip">${t.name}<br>${t.val.toFixed(3)}</span></div>`;
  h += '</div><div class="tc-labels">'; for (const t of all) h += `<span class="tc-lbl" style="width:${pct}%">${t.val.toFixed(2)}</span>`;
  return h + '</div></div>';
}
function renderTable(id, headers, rows) {
  let h = '<thead><tr>' + headers.map(x => '<th>' + x + '</th>').join('') + '</tr></thead><tbody>';
  for (const r of rows) h += '<tr>' + r.map(v => '<td>' + fmtSci(v) + '</td>').join('') + '</tr>';
  const el = document.getElementById('t-' + id); if (el) el.innerHTML = h + '</tbody>';
}
function makeChart(id, labels, datasets, xLbl, yLbl, type) {
  if (charts[id]) charts[id].destroy();
  const c = document.getElementById('c-' + id); if (!c) return;
  charts[id] = new Chart(c.getContext('2d'), { type: type || 'bar', data: { labels, datasets },
    options: { responsive: true, maintainAspectRatio: false, animation: false,
      scales: { x: { title: { display: true, text: xLbl } }, y: { title: { display: true, text: yLbl } } },
      plugins: { legend: { position: 'top' } } } });
  try { const _s=datasets.map(ds=>({label:ds.label??'Series',color:ds.borderColor??(typeof ds.backgroundColor==='string'?ds.backgroundColor:'#2563eb'),dashed:!!(ds.borderDash&&ds.borderDash.length),data:labels?labels.map((l,i)=>{const v=ds.data[i];return[typeof l==='number'?l:i,v!=null&&typeof v==='object'?(v.y??0):(v??0)];}):ds.data.map(p=>Array.isArray(p)?p:(p&&typeof p==='object'?[p.x??0,p.y??0]:[0,p??0]))}));registerReport({title:id,module:_MODULE,params:[],series:_s,xLabel:xLbl,yLabel:yLbl});} catch(_){}
}

// GeoNum precision demonstration for geophysics calculations
function geoNumDemo(value, description) {
  const ieee = value;
  const geoVal = geo(value);
  const geoResult = geoVal.toFloat64();
  const uncertainty = geoVal.getUncertainty();
  const relUncertainty = geoVal.getRelativeUncertainty();

  return `<div class="geonum-result">
    <div class="geonum-label">${description}</div>
    <div class="geonum-comparison">
      <div class="geonum-ieee">IEEE 754: ${ieee.toExponential(8)}</div>
      <div class="geonum-geo">GeoNum: ${geoResult.toExponential(8)} ± ${uncertainty.toExponential(2)}</div>
      <div class="geonum-meta">Zone: ${geoVal.zone} | Shade: ${geoVal.shade} | Drift: ${geoVal.drift.toFixed(4)} | Rel. Unc: ${(relUncertainty * 100).toExponential(2)}%</div>
    </div>
  </div>`;
}

// --- Shared WebGPU 3D viewport adapter ---------------------------------------
// Maps a scan result into a window.gdbs3D payload {points, values, lines, title}
// and exposes a "View in 3D" button next to the panel results. Guarded so a
// missing window.gdbs3D never breaks a scan. Does not alter physics or numbers.

// Revolve a 1D profile [{r, val}] around an axis into a tube point cloud,
// stacking samples along z by their index (so depth/x becomes the long axis).
function revolveProfile(samples, seg) {
  const pts = [], vals = [], segN = seg || 36;
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i], rad = Math.max(0.02, Math.abs(s.r));
    for (let k = 0; k < segN; k++) {
      const a = (k / segN) * Math.PI * 2;
      pts.push(Math.cos(a) * rad, i, Math.sin(a) * rad);
      vals.push(s.val);
    }
  }
  return { points: new Float32Array(pts), values: new Float32Array(vals) };
}

// Sample a sorted control profile [{x, y}] (monotone-by-x) onto `n` evenly
// spaced x positions by linear interpolation, so a few measured points become
// a dense curve. Returns parallel arrays {xs, ys}. No physics is invented; we
// only interpolate between the genuine engine outputs along the real axis.
function densifyProfile(ctrl, n) {
  const c = ctrl.slice().sort((a, b) => a.x - b.x);
  const xs = [], ys = [];
  if (c.length === 0) return { xs, ys };
  if (c.length === 1) { for (let i = 0; i < n; i++) { xs.push(c[0].x); ys.push(c[0].y); } return { xs, ys }; }
  const x0 = c[0].x, x1 = c[c.length - 1].x, span = (x1 - x0) || 1;
  let seg = 0;
  for (let i = 0; i < n; i++) {
    const x = x0 + span * i / (n - 1);
    while (seg < c.length - 2 && x > c[seg + 1].x) seg++;
    const a = c[seg], b = c[seg + 1];
    const t = (b.x - a.x) ? (x - a.x) / (b.x - a.x) : 0;
    xs.push(x); ys.push(a.y + (b.y - a.y) * t);
  }
  return { xs, ys };
}

// Build a flat point cloud from a family of curves: for each curve push
// (curve.x[i], curve.y[i], seriesIndex) and colour by the y value. Renders
// every sample of every curve so a multi-curve result stays dense.
function curvesToCloud(curves) {
  const pts = [], vals = [];
  for (let s = 0; s < curves.length; s++) {
    const cv = curves[s];
    for (let i = 0; i < cv.x.length; i++) { pts.push(cv.x[i], cv.y[i], s); vals.push(cv.v ? cv.v[i] : cv.y[i]); }
  }
  return { points: new Float32Array(pts), values: new Float32Array(vals) };
}

// Evaluate a closed-form model z = f(px, py) over a dense px*py grid, optionally
// overlaying the genuinely-computed items as marker points pushed last. The
// grid is the real formula the engine uses, sampled finely; markers are the
// actual scan outputs sitting on that surface.
function modelSurface(pxRange, pyRange, nx, ny, f, markers) {
  const pts = [], vals = [];
  for (let i = 0; i < nx; i++) {
    const px = pxRange[0] + (pxRange[1] - pxRange[0]) * (nx > 1 ? i / (nx - 1) : 0);
    for (let j = 0; j < ny; j++) {
      const py = pyRange[0] + (pyRange[1] - pyRange[0]) * (ny > 1 ? j / (ny - 1) : 0);
      const z = f(px, py);
      pts.push(px, py, z); vals.push(z);
    }
  }
  if (markers) for (const m of markers) { pts.push(m.x, m.y, m.z); vals.push(m.z); }
  return { points: new Float32Array(pts), values: new Float32Array(vals) };
}

function ensure3DButton(panelId, beforeId) {
  const panel = document.getElementById(panelId);
  if (!panel) return;
  const id = 'btn3d-' + panelId;
  if (document.getElementById(id)) return;
  const b = document.createElement('button');
  b.id = id;
  b.className = 'btn btn-sm';
  b.style.cssText = 'margin:6px 0;';
  b.innerHTML = '<i class="fas fa-cube"></i> View in 3D';
  b.addEventListener('click', () => {
    if (window.gdbs3D && window.gdbs3D.open && window.__gdbs3dPayload) window.gdbs3D.open(window.__gdbs3dPayload);
  });
  const anchor = beforeId ? document.getElementById(beforeId) : null;
  if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(b, anchor);
  else panel.appendChild(b);
}

// Store the mapped payload as the global 3D source and surface the button.
function set3D(panelId, beforeId, payload) {
  try {
    if (!payload) return;
    window.__gdbs3dPayload = payload;
    ensure3DButton(panelId, beforeId);
  } catch (e) { console.error('geo 3D adapter:', e); }
}

// --- Scans ---

function seismicScan() {
  status('seismic', 'Computing...'); const layers = JSON.parse(document.getElementById('in-seismic').value);
  const res = JSON.parse(run_seismic_scan(JSON.stringify({ layers, precision: 0 })));
  const r = res.results;
  renderTable('seismic', ['Layer', 'Depth (km)', 'Vp (km/s)', 'Vs (km/s)', 'K (GPa)', 'G (GPa)', 'Z', 'Coherence'], r.map(x => [x.name, x.depth_km, x.vp, x.vs, x.bulk_modulus, x.shear_modulus, x.impedance, renderGradientBar(x.tiered_coherence)]));
  makeChart('seismic', r.map(x => x.name), [
    { label: 'Vp (km/s)', data: r.map(x => x.vp), backgroundColor: '#a5d6a7' },
    { label: 'Vs (km/s)', data: r.map(x => x.vs), backgroundColor: '#66bb6a' },
  ], 'Layer', 'Velocity (km/s)');

  // GeoNum precision demo using first result's key values
  if (r.length > 0) {
    const first = r[0];
    const geoDemo = geoNumDemo(first.vp, 'P-Wave Velocity (km/s)')
      + geoNumDemo(first.vs, 'S-Wave Velocity (km/s)')
      + geoNumDemo(first.impedance, 'Acoustic Impedance');
    const demoEl = document.getElementById('geonum-demo-seismic');
    if (demoEl) demoEl.innerHTML = '<h4>GeoNum Precision Tracking</h4>' + geoDemo;
  }

  // Reference panel: PREM seismic velocities
  const refEl = document.getElementById('ref-seismic');
  if (refEl) refEl.innerHTML = renderSeismicReference();

  status('seismic', `Done — ${r.length} layers`);
  // 3D: the layered Earth velocity structure. Densify the genuine Vp(depth) and
  // Vs(depth) profiles by interpolating between the engine's layer outputs along
  // real depth, then revolve each into a tube (radius = velocity) so the result
  // is a dense velocity-vs-depth shell pair rather than one bar per layer.
  if (r.length) {
    const vpD = densifyProfile(r.map(x => ({ x: x.depth_km, y: x.vp })), 130);
    const vsD = densifyProfile(r.map(x => ({ x: x.depth_km, y: x.vs })), 130);
    const pts = [], vals = [], segN = 28;
    for (let i = 0; i < vpD.xs.length; i++) {
      const zAxis = vpD.xs[i];
      for (let k = 0; k < segN; k++) {
        const a = (k / segN) * Math.PI * 2;
        const rp = Math.max(0.02, vpD.ys[i]);
        pts.push(Math.cos(a) * rp, zAxis, Math.sin(a) * rp); vals.push(vpD.ys[i]);
        const rs = Math.max(0.02, vsD.ys[i]);
        pts.push(Math.cos(a) * rs, zAxis, Math.sin(a) * rs); vals.push(vsD.ys[i]);
      }
    }
    set3D('geop-seismic', 't-seismic', { points: new Float32Array(pts), values: new Float32Array(vals), title: 'Seismic Vp/Vs vs depth' });
  }
  animData.seismic = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('geop-seismic', 'geo-seismic', () => animData.seismic, 'Seismic Waves');
}

function stressScan() {
  status('stress', 'Computing...'); const plates = JSON.parse(document.getElementById('in-stress').value);
  const res = JSON.parse(run_stress_scan(JSON.stringify({ plates, precision: 0 })));
  const r = res.results;
  renderTable('stress', ['Plate', 'Ridge Push (N/m)', 'Basal Drag (Pa)', 'Flexural D (N\u00B7m)', 'Te (km)', 'Coherence'], r.map(x => [x.name, x.ridge_push, x.basal_drag, x.flexural_rigidity, x.elastic_thickness, renderGradientBar(x.tiered_coherence)]));
  makeChart('stress', r.map(x => x.name), [{ label: 'Te (km)', data: r.map(x => x.elastic_thickness), backgroundColor: '#a5d6a7' }], 'Plate', 'Elastic Thickness (km)');

  // Reference panel: crustal parameters
  const refEl = document.getElementById('ref-stress');
  if (refEl) refEl.innerHTML = renderStressReference();

  status('stress', `Done — ${r.length} plates`);
  // 3D: ridge-push model surface. Ridge push is the engine's closed form
  //   F_rp = g*rho_m*alpha*dT*(h*1000)^2 / 2
  // evaluated over a dense (lithospheric thickness, plate age) grid; z is its
  // log10. The genuinely-computed plates are dropped on the surface as markers.
  if (r.length) {
    const G = 9.81, RHO_M = 3300.0, ALPHA = 3.5e-5, DT = 1200.0;
    const ths = plates.map(p => p.thickness_km).filter(v => isFinite(v));
    const ages = plates.map(p => p.age_myr).filter(v => isFinite(v));
    const thMin = Math.min(20, ...ths), thMax = Math.max(220, ...ths);
    const ageMin = Math.max(0, Math.min(0, ...ages)), ageMax = Math.max(200, ...ages);
    const rp = (h_km) => { const h = h_km * 1000.0; return Math.log10(G * RHO_M * ALPHA * DT * h * h / 2.0 + 1); };
    const markers = r.map((x, i) => ({ x: plates[i] ? plates[i].thickness_km : 0, y: plates[i] ? plates[i].age_myr : 0, z: Math.log10(Math.abs(x.ridge_push) + 1) }));
    const p3 = modelSurface([thMin, thMax], [ageMin, ageMax], 48, 24, (th /*, age */) => rp(th), markers);
    p3.title = 'Ridge push vs thickness/age';
    set3D('geop-stress', 't-stress', p3);
  }
  animData.stress = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('geop-stress', 'geo-stress', () => animData.stress, 'Plate Stress');
}

function heatflowScan() {
  status('heatflow', 'Computing...'); const regions = JSON.parse(document.getElementById('in-heatflow').value);
  const res = JSON.parse(run_heatflow_scan(JSON.stringify({ regions, precision: 0 })));
  const r = res.results;
  renderTable('heatflow', ['Region', 'Moho T (\u00B0C)', 'Gradient (\u00B0C/km)', 'Coherence'], r.map(x => [x.name, x.moho_temp, x.geothermal_gradient, renderGradientBar(x.tiered_coherence)]));
  const datasets = r.map((x, i) => ({ label: x.name, data: x.geotherm.map(p => ({ x: p.depth_km, y: p.temperature })), borderColor: ['#a5d6a7','#66bb6a','#2e7d32','#c8e6c9'][i % 4], fill: false, pointRadius: 0 }));
  makeChart('heatflow', null, datasets, 'Depth (km)', 'Temperature (\u00B0C)', 'scatter');
  // Reference panel: geothermal gradient
  const refEl = document.getElementById('ref-heatflow');
  if (refEl) refEl.innerHTML = renderHeatflowReference();

  status('heatflow', `Done — ${r.length} regions`);
  // 3D: the genuine geotherms T(z) for every region as a dense family of curves.
  // Each region's 20-point engine geotherm is interpolated to a fine depth grid;
  // every region becomes one curve (depth, temperature, regionIndex), coloured
  // by temperature, so all regions' thermal structure shows at once.
  if (r.length) {
    const curves = [];
    for (const reg of r) {
      const g = reg.geotherm || [];
      if (!g.length) continue;
      const d = densifyProfile(g.map(p => ({ x: p.depth_km, y: p.temperature })), 90);
      curves.push({ x: d.xs, y: d.ys });
    }
    if (curves.length) { const p3 = curvesToCloud(curves); p3.title = 'Geotherms T(z) by region'; set3D('geop-heatflow', 't-heatflow', p3); }
  }
  animData.heatflow = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('geop-heatflow', 'geo-heatflow', () => animData.heatflow, 'Geothermal Heat');
}

function gravityScan() {
  status('gravity', 'Computing...'); const surveys = JSON.parse(document.getElementById('in-gravity').value);
  const res = JSON.parse(run_gravity_scan(JSON.stringify({ surveys, precision: 0 })));
  const r = res.results;
  renderTable('gravity', ['Name', 'Free-Air (mGal)', 'Bouguer (mGal)', 'Root (km)', 'Geoid (m)', 'Coherence'], r.map(x => [x.name, x.free_air_anomaly, x.bouguer_anomaly, x.isostatic_root, x.geoid_undulation, renderGradientBar(x.tiered_coherence)]));
  makeChart('gravity', r.map(x => x.name), [
    { label: 'Free-Air (mGal)', data: r.map(x => x.free_air_anomaly), backgroundColor: '#a5d6a7' },
    { label: 'Bouguer (mGal)', data: r.map(x => x.bouguer_anomaly), backgroundColor: '#66bb6a' },
  ], 'Survey', 'Anomaly (mGal)');
  // Reference panel: gravity constants
  const refGrav = document.getElementById('ref-gravity');
  if (refGrav) refGrav.innerHTML = renderGravityReference();

  status('gravity', `Done — ${r.length} surveys`);
  // 3D: Bouguer-anomaly model surface. The engine's closed form is
  //   bouguer = -0.3086*h - 0.04193*rho*h   (mGal, h in m, rho in g/cm^3)
  // evaluated over a dense (elevation, crustal density) grid; the surveyed
  // sites are dropped on it as markers at their actual computed Bouguer value.
  if (r.length) {
    const elevs = surveys.map(s => s.elevation_m).filter(v => isFinite(v));
    const dens = surveys.map(s => s.crustal_density).filter(v => isFinite(v));
    const hMin = Math.min(-500, ...elevs), hMax = Math.max(4000, ...elevs);
    const rMin = Math.min(2.4, ...dens), rMax = Math.max(3.0, ...dens);
    const bg = (h, rho) => (-0.3086 * h) - (0.04193 * rho * h);
    const markers = r.map((x, i) => ({ x: surveys[i] ? surveys[i].elevation_m : 0, y: surveys[i] ? surveys[i].crustal_density : 0, z: x.bouguer_anomaly || 0 }));
    const p3 = modelSurface([hMin, hMax], [rMin, rMax], 48, 24, (h, rho) => bg(h, rho), markers);
    p3.title = 'Bouguer anomaly vs elevation/density';
    set3D('geop-gravity', 't-gravity', p3);
  }
  animData.gravity = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('geop-gravity', 'geo-gravity', () => animData.gravity, 'Gravity Anomaly');
}

function earthquakeScan() {
  status('earthquake', 'Computing...'); const zones = JSON.parse(document.getElementById('in-earthquake').value);
  const res = JSON.parse(run_earthquake_scan(JSON.stringify({ zones, precision: 0 })));
  const r = res.results;
  renderTable('earthquake', ['Zone', 'Mmax', 'Return (yr)', 'PGA (g)', 'M\u2080 (N\u00B7m)', 'b', 'Coherence'], r.map(x => [x.name, x.mmax, x.return_period_mmax, x.pga_at_10km, x.moment_mmax, x.b_value, renderGradientBar(x.tiered_coherence)]));
  makeChart('earthquake', r.map(x => x.name), [{ label: 'Mmax', data: r.map(x => x.mmax), backgroundColor: '#a5d6a7' }, { label: 'Return (log yr)', data: r.map(x => Math.log10(x.return_period_mmax)), backgroundColor: '#66bb6a' }], 'Zone', 'Value');
  // Reference panel: Gutenberg-Richter & PGA
  const refEq = document.getElementById('ref-earthquake');
  if (refEq) refEq.innerHTML = renderEarthquakeReference();

  status('earthquake', `Done — ${r.length} zones`);
  // 3D: Gutenberg-Richter recurrence curves. For each zone the engine uses
  //   N(M) = 10^(a - b*M),  return period T_r = 1/N
  // sampled densely over magnitude M from 4 up to the zone's Mmax. Each zone is
  // one curve (magnitude, log10 return period, zoneIndex), coloured by return
  // period, so the full frequency-magnitude structure is shown, not one bar.
  if (r.length) {
    const curves = [];
    for (let i = 0; i < r.length; i++) {
      const z = zones[i] || {}; const a = z.a_value, b = z.b_value, mmax = (r[i].mmax != null ? r[i].mmax : z.mmax) || 8;
      if (a == null || b == null) continue;
      const mMin = 4.0, np = 100, xs = [], ys = [];
      for (let k = 0; k < np; k++) {
        const M = mMin + (mmax - mMin) * k / (np - 1);
        const N = Math.pow(10, a - b * M);
        const Tr = N > 0 ? 1.0 / N : 1e6;
        xs.push(M); ys.push(Math.log10(Tr));
      }
      curves.push({ x: xs, y: ys });
    }
    if (curves.length) { const p3 = curvesToCloud(curves); p3.title = 'G-R recurrence by zone'; set3D('geop-earthquake', 't-earthquake', p3); }
    else { // fallback: genuinely only per-zone scalars available
      const pts = [], vals = [];
      r.forEach((x, i) => { pts.push(i, x.mmax || 0, x.pga_at_10km || 0); vals.push(x.mmax || 0); });
      set3D('geop-earthquake', 't-earthquake', { points: new Float32Array(pts), values: new Float32Array(vals), title: 'Seismic hazard (Mmax)' });
    }
  }
  animData.earthquake = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('geop-earthquake', 'geo-earthquake', () => animData.earthquake, 'Earthquake Hazard');
}

// ============================================================================
// Experimental / Literature Reference Panels
// ============================================================================

const REF_STYLE = 'margin-top:10px;padding:10px 14px;background:#1a2332;border:1px solid #2a3a4a;border-radius:6px;font-size:0.82rem;color:#b0bec5;line-height:1.6';
const LINK = (url, text) => `<a href="${url}" target="_blank" style="color:#4fc3f7">${text}</a>`;

function renderSeismicReference() {
  const prem = LINK('https://doi.org/10.1016/0031-9201(81)90046-7', 'PREM (Dziewonski & Anderson 1981)');
  const moho = LINK('https://doi.org/10.1029/95JB00259', 'Christensen & Mooney 1995');
  return `<div style="${REF_STYLE}">
    <strong style="color:#e0e0e0">Reference Values &mdash; Seismic Velocities</strong>
    <table style="width:100%;margin-top:6px;border-collapse:collapse;font-size:0.8rem">
      <thead><tr style="color:#81c784">
        <th style="text-align:left;padding:3px 6px">Layer</th>
        <th style="text-align:right;padding:3px 6px">Vp (km/s)</th>
        <th style="text-align:right;padding:3px 6px">Vs (km/s)</th>
        <th style="text-align:left;padding:3px 6px">Source</th>
      </tr></thead>
      <tbody style="color:#b0bec5">
        <tr><td style="padding:2px 6px">Upper Mantle</td><td style="text-align:right;padding:2px 6px">8.08</td><td style="text-align:right;padding:2px 6px">4.47</td><td style="padding:2px 6px">${prem}</td></tr>
        <tr><td style="padding:2px 6px">Continental Crust</td><td style="text-align:right;padding:2px 6px">6.50</td><td style="text-align:right;padding:2px 6px">3.60</td><td style="padding:2px 6px">${prem}</td></tr>
      </tbody>
    </table>
    <div style="margin-top:6px">
      <span style="color:#90a4ae">Moho depth (continental):</span> 35 km &mdash; ${moho}<br>
      <span style="color:#90a4ae">Moho depth (oceanic):</span> 7 km &mdash; ${moho}<br>
      <span style="color:#90a4ae">Earth mean density:</span> 5515 kg/m&sup3; &mdash; ${LINK('https://physics.nist.gov/cgi-bin/cuu/Value?gn', 'NIST')}
    </div>
  </div>`;
}

function renderStressReference() {
  const moho = LINK('https://doi.org/10.1029/95JB00259', 'Christensen & Mooney 1995');
  return `<div style="${REF_STYLE}">
    <strong style="color:#e0e0e0">Reference Values &mdash; Crustal / Plate Parameters</strong>
    <div style="margin-top:6px">
      <span style="color:#90a4ae">Avg continental Moho depth:</span> 35 km &mdash; ${moho}<br>
      <span style="color:#90a4ae">Avg oceanic Moho depth:</span> 7 km &mdash; ${moho}<br>
      <span style="color:#90a4ae">Standard gravity:</span> 9.80665 m/s&sup2; &mdash; ${LINK('https://physics.nist.gov/cgi-bin/cuu/Value?gn', 'NIST')}<br>
      <span style="color:#90a4ae">Earth mean density:</span> 5515 kg/m&sup3;
    </div>
  </div>`;
}

function renderHeatflowReference() {
  const prem = LINK('https://doi.org/10.1016/0031-9201(81)90046-7', 'PREM (Dziewonski & Anderson 1981)');
  return `<div style="${REF_STYLE}">
    <strong style="color:#e0e0e0">Reference Values &mdash; Geothermal</strong>
    <div style="margin-top:6px">
      <span style="color:#90a4ae">Normal geothermal gradient:</span> 25&ndash;30 &deg;C/km &mdash; ${LINK('https://doi.org/10.1017/CBO9780511843877', 'Turcotte & Schubert, <em>Geodynamics</em>')}<br>
      <span style="color:#90a4ae">Continental Moho temp (typical):</span> ~600&ndash;900 &deg;C at 35 km depth<br>
      <span style="color:#90a4ae">Mantle reference:</span> ${prem}
    </div>
  </div>`;
}

function renderGravityReference() {
  return `<div style="${REF_STYLE}">
    <strong style="color:#e0e0e0">Reference Values &mdash; Gravity</strong>
    <div style="margin-top:6px">
      <span style="color:#90a4ae">Free-air anomaly at sea level:</span> ~0 mGal (by definition)<br>
      <span style="color:#90a4ae">Standard gravity g<sub>0</sub>:</span> 9.80665 m/s&sup2; &mdash; ${LINK('https://physics.nist.gov/cgi-bin/cuu/Value?gn', 'NIST')}<br>
      <span style="color:#90a4ae">Earth mean density:</span> 5515 kg/m&sup3;<br>
      <span style="color:#90a4ae">Bouguer correction density (std):</span> 2670 kg/m&sup3; &mdash; ${LINK('https://doi.org/10.1016/0031-9201(81)90046-7', 'PREM')}<br>
      <span style="color:#90a4ae">Geoid model:</span> ${LINK('https://earth-info.nga.mil/index.php?dir=wgs84&action=wgs84', 'EGM2008 (NGA/WGS84)')}
    </div>
  </div>`;
}

function renderEarthquakeReference() {
  return `<div style="${REF_STYLE}">
    <strong style="color:#e0e0e0">Reference Values &mdash; Seismicity</strong>
    <div style="margin-top:6px">
      <span style="color:#90a4ae">Gutenberg-Richter b-value (global avg):</span> 1.0 &pm; 0.1 &mdash; ${LINK('https://doi.org/10.1785/BSSA0340040185', 'Gutenberg & Richter 1944')}<br>
      <span style="color:#90a4ae">PGA &harr; intensity correlations:</span> ${LINK('https://www.usgs.gov/programs/earthquake-hazards/modified-mercalli-intensity-scale', 'Modified Mercalli Scale (USGS)')}<br>
    </div>
    <table style="width:100%;margin-top:6px;border-collapse:collapse;font-size:0.8rem">
      <thead><tr style="color:#81c784">
        <th style="text-align:left;padding:3px 6px">MMI</th>
        <th style="text-align:right;padding:3px 6px">PGA (g)</th>
        <th style="text-align:left;padding:3px 6px">Perceived</th>
      </tr></thead>
      <tbody style="color:#b0bec5">
        <tr><td style="padding:2px 6px">V</td><td style="text-align:right;padding:2px 6px">0.03&ndash;0.04</td><td style="padding:2px 6px">Moderate</td></tr>
        <tr><td style="padding:2px 6px">VII</td><td style="text-align:right;padding:2px 6px">0.10&ndash;0.20</td><td style="padding:2px 6px">Very strong</td></tr>
        <tr><td style="padding:2px 6px">IX</td><td style="text-align:right;padding:2px 6px">0.50&ndash;1.00</td><td style="padding:2px 6px">Violent</td></tr>
      </tbody>
    </table>
  </div>`;
}
